'use client';

import { useRef, useState, KeyboardEvent, ClipboardEvent, useEffect } from 'react';
import { cn } from '@/lib/utils/cn';

interface OTPInputProps {
  length?: number;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  error?: boolean;
}

export function OTPInput({
  length = 6,
  value,
  onChange,
  disabled = false,
  error = false,
}: OTPInputProps) {
  const [otp, setOtp] = useState<string[]>(Array(length).fill(''));
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Update internal state when value prop changes
  useEffect(() => {
    if (value.length === 0) {
      setOtp(Array(length).fill(''));
    } else {
      setOtp(value.split('').concat(Array(length).fill('')).slice(0, length));
    }
  }, [value, length]);

  const focusInput = (index: number) => {
    const input = inputRefs.current[index];
    if (input) {
      input.focus();
      input.select();
    }
  };

  const handleChange = (index: number, newValue: string) => {
    if (disabled) return;

    // Only allow digits
    const sanitized = newValue.replace(/[^0-9]/g, '');
    if (sanitized.length === 0) return;

    const newOtp = [...otp];

    // Handle paste or multiple characters
    if (sanitized.length > 1) {
      const chars = sanitized.slice(0, length - index).split('');
      chars.forEach((char, i) => {
        if (index + i < length) {
          newOtp[index + i] = char;
        }
      });
      setOtp(newOtp);
      onChange(newOtp.join(''));

      // Focus the next empty input or the last input
      const nextEmptyIndex = newOtp.findIndex((val, i) => i > index && val === '');
      if (nextEmptyIndex !== -1) {
        focusInput(nextEmptyIndex);
      } else {
        focusInput(Math.min(index + chars.length, length - 1));
      }
    } else {
      // Single character
      newOtp[index] = sanitized[0];
      setOtp(newOtp);
      onChange(newOtp.join(''));

      // Move to next input if not the last one
      if (index < length - 1) {
        focusInput(index + 1);
      }
    }
  };

  const handleKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (disabled) return;

    if (e.key === 'Backspace') {
      e.preventDefault();
      const newOtp = [...otp];

      if (otp[index]) {
        // Clear current input
        newOtp[index] = '';
        setOtp(newOtp);
        onChange(newOtp.join(''));
      } else if (index > 0) {
        // Move to previous input and clear it
        newOtp[index - 1] = '';
        setOtp(newOtp);
        onChange(newOtp.join(''));
        focusInput(index - 1);
      }
    } else if (e.key === 'ArrowLeft' && index > 0) {
      e.preventDefault();
      focusInput(index - 1);
    } else if (e.key === 'ArrowRight' && index < length - 1) {
      e.preventDefault();
      focusInput(index + 1);
    } else if (e.key === 'Delete') {
      e.preventDefault();
      const newOtp = [...otp];
      newOtp[index] = '';
      setOtp(newOtp);
      onChange(newOtp.join(''));
    }
  };

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    if (disabled) return;

    e.preventDefault();
    const pastedData = e.clipboardData.getData('text/plain');
    const sanitized = pastedData.replace(/[^0-9]/g, '').slice(0, length);

    if (sanitized.length > 0) {
      const newOtp = sanitized.split('').concat(Array(length).fill('')).slice(0, length);
      setOtp(newOtp);
      onChange(newOtp.join(''));

      // Focus the next empty input or the last input
      const nextEmptyIndex = newOtp.findIndex((val) => val === '');
      if (nextEmptyIndex !== -1) {
        focusInput(nextEmptyIndex);
      } else {
        focusInput(length - 1);
      }
    }
  };

  const handleFocus = (index: number) => {
    inputRefs.current[index]?.select();
  };

  return (
    <div className="flex gap-2 justify-center">
      {otp.map((digit, index) => (
        <input
          key={index}
          ref={(el) => {
            inputRefs.current[index] = el;
          }}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={digit}
          onChange={(e) => handleChange(index, e.target.value)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onPaste={handlePaste}
          onFocus={() => handleFocus(index)}
          disabled={disabled}
          className={cn(
            'w-12 h-14 text-center text-2xl font-bold rounded-lg border-2 transition-all',
            'focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-white',
            error
              ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
              : 'border-ink-950/10 focus:border-brand-500 focus:ring-brand-500',
            disabled
              ? 'bg-ink-100 cursor-not-allowed opacity-50'
              : 'bg-white hover:border-brand-200',
            digit
              ? 'border-brand-500 text-ink-950'
              : 'text-ink-400'
          )}
          autoComplete="off"
        />
      ))}
    </div>
  );
}
